import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  CircleAlert,
  Database,
  Download,
  FileClock,
  Globe2,
  HardDrive,
  ImageUp,
  Newspaper,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  ShieldCheck,
  Trash2,
  Wifi,
} from "lucide-react";
import {
  useEffect,
  useState,
  type CSSProperties,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from "react";
import { useSearchParams } from "react-router-dom";
import { api, prepareSystemBackupDownload, uploadPlatformAsset } from "./api.js";
import { ColorPicker } from "./ColorPicker.js";
import { useModalFocus } from "./useModalFocus.js";

export interface PlatformBranding {
  name: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  playerUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  environmentMode: "development" | "production";
  clientDashboardEnabled: boolean;
}

function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="notice notice--error" role="alert">
      <CircleAlert size={18} />
      <span>{error instanceof Error ? error.message : "The request could not be completed."}</span>
    </div>
  );
}

function Loading() {
  return (
    <div className="page-loading" role="status">
      <span className="spinner" />
      <span>Loading operational data…</span>
    </div>
  );
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "Never";
}

function isSafeExternalUrl(value: string | null) {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

interface RssSource {
  id: string;
  name: string;
  url: string;
  status: "active" | "disabled";
  refreshIntervalMinutes: number;
  entryCount: number;
  lastSuccessAt: string | null;
  lastError: string | null;
  global: boolean;
  clientIds: string[];
}

interface RssClientOption {
  id: string;
  name: string;
  effectiveStatus: string;
}

interface RssEntryPreview {
  id: string;
  title: string;
  description: string | null;
  link: string | null;
  publishedAt: string | null;
  fetchedAt: string;
}

export function AdminRssPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [interval, setInterval] = useState(60);
  const [showCreate, setShowCreate] = useState(false);
  const [accessMode, setAccessMode] = useState<"global" | "clients">("global");
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [editingAccess, setEditingAccess] = useState<RssSource | null>(null);
  const [editAccessMode, setEditAccessMode] = useState<"global" | "clients">("global");
  const [editClientIds, setEditClientIds] = useState<string[]>([]);
  const [previewingSource, setPreviewingSource] = useState<RssSource | null>(null);
  const query = useQuery({
    queryKey: ["admin", "rss-sources"],
    queryFn: () => api<RssSource[]>("/api/v1/admin/rss-sources"),
  });
  const clientsQuery = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => api<RssClientOption[]>("/api/v1/admin/clients"),
  });
  const entriesQuery = useQuery({
    queryKey: ["admin", "rss-sources", previewingSource?.id, "entries"],
    queryFn: () =>
      api<RssEntryPreview[]>(`/api/v1/admin/rss-sources/${previewingSource?.id}/entries`),
    enabled: Boolean(previewingSource),
  });
  const refresh = useMutation({
    mutationFn: () =>
      api<{ refreshed: number; failed: number }>("/api/v1/admin/rss-sources/refresh", {
        method: "POST",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "rss-sources"] }),
  });
  const create = useMutation({
    mutationFn: () =>
      api<RssSource>("/api/v1/admin/rss-sources", {
        method: "POST",
        body: JSON.stringify({
          name,
          url,
          refreshIntervalMinutes: interval,
          global: accessMode === "global",
          clientIds: accessMode === "clients" ? selectedClientIds : [],
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "rss-sources"] });
      setName("");
      setUrl("");
      setInterval(60);
      setAccessMode("global");
      setSelectedClientIds([]);
      setShowCreate(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, status }: Pick<RssSource, "id" | "status">) =>
      api(`/api/v1/admin/rss-sources/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "rss-sources"] }),
  });
  const updateAccess = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/rss-sources/${editingAccess?.id ?? ""}/assignments`, {
        method: "PUT",
        body: JSON.stringify({
          global: editAccessMode === "global",
          clientIds: editAccessMode === "clients" ? editClientIds : [],
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "rss-sources"] });
      setEditingAccess(null);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  function toggleClient(
    clientId: string,
    checked: boolean,
    setter: Dispatch<SetStateAction<string[]>>,
  ): void {
    setter((current) =>
      checked ? [...new Set([...current, clientId])] : current.filter((id) => id !== clientId),
    );
  }

  function openAccess(source: RssSource): void {
    setEditingAccess(source);
    setEditAccessMode(source.global ? "global" : "clients");
    setEditClientIds(source.clientIds);
  }

  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>RSS source operations</h2>
          <p>Register trusted feeds, control refresh eligibility, and review ingestion health.</p>
        </div>
        <div className="button-row">
          <button
            type="button"
            className="button button--secondary"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <RefreshCw size={17} /> {refresh.isPending ? "Refreshing…" : "Refresh all"}
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setShowCreate((value) => !value)}
          >
            <Plus size={17} /> Add source
          </button>
        </div>
      </section>

      {showCreate ? (
        <form className="card operation-form" onSubmit={submit}>
          <div>
            <h3>Add a trusted source</h3>
            <p className="muted">Private and local network addresses are rejected by the API.</p>
          </div>
          <div className="form-grid form-grid--three">
            <label>
              <span>Source name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            <label>
              <span>Public feed URL</span>
              <input
                type="url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://example.com/feed.xml"
                required
              />
            </label>
            <label>
              <span>Refresh interval (minutes)</span>
              <input
                type="number"
                min={15}
                max={10080}
                value={interval}
                onChange={(event) => setInterval(Number(event.target.value))}
                required
              />
            </label>
          </div>
          <label>
            <span>Client availability</span>
            <select
              value={accessMode}
              onChange={(event) => {
                const mode = event.currentTarget.value as "global" | "clients";
                setAccessMode(mode);
                if (mode === "global") setSelectedClientIds([]);
              }}
            >
              <option value="global">All clients</option>
              <option value="clients">Selected clients only</option>
            </select>
          </label>
          {accessMode === "clients" ? (
            <fieldset className="rss-client-access">
              <legend>Select clients</legend>
              {clientsQuery.isLoading ? (
                <span className="muted">Loading clients…</span>
              ) : (
                clientsQuery.data?.map((client) => (
                  <label className="checkbox-field" key={client.id}>
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(client.id)}
                      onChange={(event) =>
                        toggleClient(client.id, event.target.checked, setSelectedClientIds)
                      }
                    />
                    <span>
                      <strong>{client.name}</strong>
                      <small>{client.effectiveStatus}</small>
                    </span>
                  </label>
                ))
              )}
            </fieldset>
          ) : null}
          <ErrorNotice error={create.error} />
          <div className="modal-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={
                create.isPending || (accessMode === "clients" && selectedClientIds.length === 0)
              }
            >
              {create.isPending ? "Adding…" : "Add RSS source"}
            </button>
          </div>
        </form>
      ) : null}

      <ErrorNotice error={refresh.error ?? update.error} />
      {refresh.data ? (
        <div className={refresh.data.failed ? "notice notice--error" : "notice notice--good"}>
          <Newspaper size={18} />
          <span>
            Refreshed {refresh.data.refreshed} source(s); {refresh.data.failed} failed.
          </span>
        </div>
      ) : null}
      <section className="card table-card">
        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorNotice error={query.error} />
        ) : !query.data?.length ? (
          <div className="empty-state">
            <Newspaper size={28} />
            <h3>No RSS sources yet</h3>
            <p>Add the first trusted public feed to make ticker content available.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Entries</th>
                  <th>Availability</th>
                  <th>Last success</th>
                  <th>Refresh</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((source) => (
                  <tr key={source.id}>
                    <td>
                      <strong>{source.name}</strong>
                      <small className="cell-subtitle">{source.url}</small>
                      {source.lastError ? (
                        <small className="cell-error">{source.lastError}</small>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${source.status === "active" ? "good" : "warn"}`}
                      >
                        {source.status}
                      </span>
                    </td>
                    <td>{source.entryCount}</td>
                    <td>
                      <span
                        className={`status-pill status-pill--${
                          source.global ? "good" : source.clientIds.length ? "neutral" : "warn"
                        }`}
                      >
                        {source.global
                          ? "All clients"
                          : source.clientIds.length
                            ? `${source.clientIds.length} client${
                                source.clientIds.length === 1 ? "" : "s"
                              }`
                            : "Not assigned"}
                      </span>
                    </td>
                    <td>{formatDate(source.lastSuccessAt)}</td>
                    <td>Every {source.refreshIntervalMinutes} min</td>
                    <td>
                      <div className="button-row">
                        <button
                          type="button"
                          className="button button--small button--secondary"
                          onClick={() => setPreviewingSource(source)}
                        >
                          Preview entries
                        </button>
                        <button
                          type="button"
                          className="button button--small button--secondary"
                          onClick={() => openAccess(source)}
                        >
                          Manage access
                        </button>
                        <button
                          type="button"
                          className="button button--small button--secondary"
                          disabled={update.isPending}
                          onClick={() =>
                            update.mutate({
                              id: source.id,
                              status: source.status === "active" ? "disabled" : "active",
                            })
                          }
                        >
                          {source.status === "active" ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {previewingSource ? (
        <section
          className="card operation-form"
          aria-label={`Entries for ${previewingSource.name}`}
        >
          <div className="section-heading">
            <div>
              <h3>Entries for {previewingSource.name}</h3>
              <p className="muted">Latest 50 cached entries, newest first.</p>
            </div>
            <button
              type="button"
              className="button button--secondary button--small"
              onClick={() => setPreviewingSource(null)}
            >
              Close preview
            </button>
          </div>
          {entriesQuery.isLoading ? <Loading /> : null}
          {entriesQuery.error ? <ErrorNotice error={entriesQuery.error} /> : null}
          {!entriesQuery.isLoading && !entriesQuery.error && !entriesQuery.data?.length ? (
            <div className="empty-state">
              <Newspaper size={24} />
              <h4>No cached entries</h4>
              <p>Refresh RSS sources to fetch the latest headlines.</p>
            </div>
          ) : null}
          {entriesQuery.data?.length ? (
            <div className="rss-entry-preview-list">
              {entriesQuery.data.map((entry) => (
                <article key={entry.id}>
                  <div>
                    <strong>{entry.title}</strong>
                    <small>
                      Published {formatDate(entry.publishedAt)} · fetched{" "}
                      {formatDate(entry.fetchedAt)}
                    </small>
                  </div>
                  {entry.description ? <p>{entry.description}</p> : null}
                  {isSafeExternalUrl(entry.link) ? (
                    <a href={entry.link ?? undefined} target="_blank" rel="noreferrer">
                      Open article
                    </a>
                  ) : null}
                </article>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}
      {editingAccess ? (
        <form
          className="card operation-form"
          onSubmit={(event) => {
            event.preventDefault();
            updateAccess.mutate();
          }}
        >
          <div>
            <h3>Client access for {editingAccess.name}</h3>
            <p className="muted">
              Only assigned clients can select this source in their playlist ticker settings.
            </p>
          </div>
          <div className="rss-access-mode">
            <label className="checkbox-field">
              <input
                type="radio"
                name="rss-access-mode"
                checked={editAccessMode === "global"}
                onChange={() => {
                  setEditAccessMode("global");
                  setEditClientIds([]);
                }}
              />
              <Globe2 size={17} />
              <span>
                <strong>All clients</strong>
                <small>Make this source available platform-wide.</small>
              </span>
            </label>
            <label className="checkbox-field">
              <input
                type="radio"
                name="rss-access-mode"
                checked={editAccessMode === "clients"}
                onChange={() => setEditAccessMode("clients")}
              />
              <Building2 size={17} />
              <span>
                <strong>Selected clients</strong>
                <small>Keep the source limited to specific customer tenants.</small>
              </span>
            </label>
          </div>
          {editAccessMode === "clients" ? (
            <fieldset className="rss-client-access">
              <legend>Available clients</legend>
              {clientsQuery.data?.map((client) => (
                <label className="checkbox-field" key={client.id}>
                  <input
                    type="checkbox"
                    checked={editClientIds.includes(client.id)}
                    onChange={(event) =>
                      toggleClient(client.id, event.target.checked, setEditClientIds)
                    }
                  />
                  <span>
                    <strong>{client.name}</strong>
                    <small>{client.effectiveStatus}</small>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}
          <ErrorNotice error={updateAccess.error} />
          <div className="modal-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setEditingAccess(null)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={
                updateAccess.isPending ||
                (editAccessMode === "clients" && editClientIds.length === 0)
              }
            >
              {updateAccess.isPending ? "Saving…" : "Save access"}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

interface CacheInventory {
  globalVersion: number;
  clients: Array<{ id: string; name: string; cacheVersion: number }>;
  tvs: Array<{ id: string; name: string; clientName: string; cacheVersion: number }>;
}

export function AdminCachePage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "cache"],
    queryFn: () => api<CacheInventory>("/api/v1/admin/cache"),
  });
  const [clientId, setClientId] = useState("");
  const [tvId, setTvId] = useState("");
  useEffect(() => {
    if (!clientId && query.data?.clients[0]) setClientId(query.data.clients[0].id);
    if (!tvId && query.data?.tvs[0]) setTvId(query.data.tvs[0].id);
  }, [clientId, query.data, tvId]);
  const reset = useMutation({
    mutationFn: (path: string) => api(path, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "cache"] }),
  });
  const selectedClient = query.data?.clients.find((client) => client.id === clientId);
  const selectedTv = query.data?.tvs.find((tv) => tv.id === tvId);

  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Versioned cache controls</h2>
          <p>Force precise refreshes without deleting the player’s last-known-good media.</p>
        </div>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw size={17} /> Refresh inventory
        </button>
      </section>
      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : (
        <div className="operation-grid">
          <article className="card operation-card operation-card--featured">
            <span className="operation-icon">
              <Database size={22} />
            </span>
            <div>
              <span className="eyebrow">Platform namespace</span>
              <h3>Global version {query.data?.globalVersion}</h3>
              <p>All players will compare against a new platform cache version.</p>
            </div>
            <button
              type="button"
              className="button button--primary"
              disabled={reset.isPending}
              onClick={() => reset.mutate("/api/v1/admin/cache/global/reset")}
            >
              Increment global version
            </button>
          </article>
          <article className="card operation-card">
            <span className="operation-icon">
              <ShieldCheck size={22} />
            </span>
            <div>
              <h3>Customer cache</h3>
              <p>Refresh every display belonging to one customer.</p>
            </div>
            <label>
              <span>Customer</span>
              <select value={clientId} onChange={(event) => setClientId(event.target.value)}>
                {query.data?.clients.map((client) => (
                  <option value={client.id} key={client.id}>
                    {client.name} · v{client.cacheVersion}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="button button--secondary"
              disabled={!selectedClient || reset.isPending}
              onClick={() => reset.mutate(`/api/v1/admin/cache/clients/${clientId}/reset`)}
            >
              Increment {selectedClient?.name ?? "customer"}
            </button>
          </article>
          <article className="card operation-card">
            <span className="operation-icon">
              <Wifi size={22} />
            </span>
            <div>
              <h3>Single-TV cache</h3>
              <p>Target one screen without affecting its customer’s other TVs.</p>
            </div>
            <label>
              <span>Television</span>
              <select value={tvId} onChange={(event) => setTvId(event.target.value)}>
                {query.data?.tvs.map((tv) => (
                  <option value={tv.id} key={tv.id}>
                    {tv.clientName} / {tv.name} · v{tv.cacheVersion}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="button button--secondary"
              disabled={!selectedTv || reset.isPending}
              onClick={() => reset.mutate(`/api/v1/admin/cache/tvs/${tvId}/reset`)}
            >
              Increment {selectedTv?.name ?? "TV"}
            </button>
          </article>
        </div>
      )}
      <ErrorNotice error={reset.error} />
    </div>
  );
}

interface AuditRow {
  id: string;
  actorType: string;
  actorId: string | null;
  clientName: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  requestId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export function AdminAuditPage() {
  const pageSize = 50;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const search = searchParams.get("search") ?? "";
  const query = useQuery({
    queryKey: ["admin", "audit-logs", page, search],
    queryFn: () =>
      api<{ rows: AuditRow[]; total: number; limit: number; offset: number }>(
        `/api/v1/admin/audit-logs?paged=1&limit=${pageSize}&offset=${(page - 1) * pageSize}&search=${encodeURIComponent(search)}`,
      ),
  });
  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updatePage = (nextPage: number): void => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next);
  };

  useEffect(() => {
    if (!query.data || page <= totalPages) return;
    const next = new URLSearchParams(searchParams);
    if (totalPages <= 1) next.delete("page");
    else next.set("page", String(totalPages));
    setSearchParams(next, { replace: true });
  }, [page, query.data, searchParams, setSearchParams, totalPages]);

  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Immutable activity trail</h2>
          <p>Review actor, target, request ID, tenant context, and recorded change metadata.</p>
        </div>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw size={17} /> Refresh
        </button>
      </section>
      <label className="search-field">
        <Search size={18} />
        <input
          value={search}
          onChange={(event) => {
            const next = new URLSearchParams(searchParams);
            const value = event.target.value;
            if (value) next.set("search", value);
            else next.delete("search");
            next.delete("page");
            setSearchParams(next, { replace: true });
          }}
          placeholder="Search action, client, target, or request ID"
        />
      </label>
      <section className="card table-card">
        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorNotice error={query.error} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Actor</th>
                  <th>Customer</th>
                  <th>Target</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <strong>{row.action}</strong>
                    </td>
                    <td>{row.actorType}</td>
                    <td>{row.clientName ?? "Platform"}</td>
                    <td>
                      {row.targetType}
                      {row.targetId ? (
                        <small className="cell-subtitle">{row.targetId}</small>
                      ) : null}
                    </td>
                    <td>
                      <details className="audit-details">
                        <summary>View details</summary>
                        <dl>
                          <dt>Request ID</dt>
                          <dd>{row.requestId}</dd>
                          <dt>Metadata</dt>
                          <dd>
                            <pre>{JSON.stringify(row.metadata, null, 2)}</pre>
                          </dd>
                        </dl>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length ? (
              <p className="empty-copy">No audit entries match this search.</p>
            ) : null}
          </div>
        )}
      </section>
      {!query.isLoading && !query.error ? (
        <nav className="table-pagination" aria-label="Audit log pages">
          <span>
            {total
              ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`
              : "0 entries"}
          </span>
          <div>
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={page <= 1 || query.isFetching}
              onClick={() => updatePage(page - 1)}
            >
              Previous
            </button>
            <span aria-live="polite">
              Page {Math.min(page, totalPages)} of {totalPages}
            </span>
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={page >= totalPages || query.isFetching}
              onClick={() => updatePage(page + 1)}
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}

interface HealthResponse {
  status: "healthy" | "degraded";
  checkedAt: string;
  services: Array<{
    name: string;
    status: "healthy" | "degraded";
    latencyMs: number;
    message?: string;
  }>;
  counts: { clients: number; tvs: number; slides: number };
  jobs: Array<{
    id: string;
    jobName: string;
    outcome: string;
    startedAt: string;
    finishedAt: string | null;
    message: string | null;
  }>;
}

const serviceIcons = [Database, Server, HardDrive];

export function AdminHealthPage() {
  const query = useQuery({
    queryKey: ["admin", "system-health"],
    queryFn: () => api<HealthResponse>("/api/v1/admin/system-health"),
    refetchInterval: 60_000,
  });
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Live dependency health</h2>
          <p>Each check exercises the real database, cache, and object storage connection.</p>
        </div>
        <button
          type="button"
          className="button button--secondary"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw size={17} /> Run checks now
        </button>
      </section>
      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorNotice error={query.error} />
      ) : query.data ? (
        <>
          <div className={`health-banner health-banner--${query.data.status}`}>
            <Activity size={24} />
            <div>
              <strong>Platform is {query.data.status}</strong>
              <span>Checked {formatDate(query.data.checkedAt)}</span>
            </div>
          </div>
          <div className="health-grid">
            {query.data.services.map((service, index) => {
              const Icon = serviceIcons[index] ?? Server;
              return (
                <article className="card health-card" key={service.name}>
                  <Icon size={23} />
                  <div>
                    <h3>{service.name}</h3>
                    <p>{service.message ?? "Read/write probe completed successfully."}</p>
                  </div>
                  <span
                    className={`status-pill status-pill--${service.status === "healthy" ? "good" : "warn"}`}
                  >
                    {service.status} · {service.latencyMs} ms
                  </span>
                </article>
              );
            })}
          </div>
          <div className="metric-grid metric-grid--three">
            <article className="metric-card">
              <strong>{query.data.counts.clients}</strong>
              <span>Customers</span>
            </article>
            <article className="metric-card">
              <strong>{query.data.counts.tvs}</strong>
              <span>TVs</span>
            </article>
            <article className="metric-card">
              <strong>{query.data.counts.slides}</strong>
              <span>Slides</span>
            </article>
          </div>
          <section className="card table-card">
            <header className="section-head">
              <div>
                <h3>Background jobs</h3>
                <p>Most recent worker outcomes.</p>
              </div>
              <FileClock size={21} />
            </header>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Outcome</th>
                    <th>Started</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.jobs.map((job) => (
                    <tr key={job.id}>
                      <td>
                        <strong>{job.jobName}</strong>
                      </td>
                      <td>
                        <span
                          className={`status-pill status-pill--${job.outcome === "success" ? "good" : "warn"}`}
                        >
                          {job.outcome}
                        </span>
                      </td>
                      <td>{formatDate(job.startedAt)}</td>
                      <td>{job.message ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!query.data.jobs.length ? (
                <p className="empty-copy">No background job has run yet.</p>
              ) : null}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

interface UploadedImage {
  publicUrl: string;
}

interface PortableBackupJob {
  id: string;
  status: "queued" | "processing" | "ready" | "failed" | "expired";
  filename: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  errorMessage: string | null;
  downloadCount: number;
  lastDownloadedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatBytes(value: number | null) {
  if (value === null) return "";
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KB`;
  if (value < 1_073_741_824) return `${(value / 1_048_576).toFixed(1)} MB`;
  return `${(value / 1_073_741_824).toFixed(2)} GB`;
}

export function AdminSettingsPage({
  branding,
  onFactoryReset,
}: {
  branding: PlatformBranding;
  onFactoryReset(): void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api<PlatformBranding>("/api/v1/admin/settings"),
    placeholderData: branding,
    staleTime: 0,
  });
  const [form, setForm] = useState<PlatformBranding>(branding);
  const [formTouched, setFormTouched] = useState(false);
  const [uploading, setUploading] = useState<"logo" | "favicon" | null>(null);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState("");
  const [backupDeleteTarget, setBackupDeleteTarget] = useState<PortableBackupJob | null>(null);
  useEffect(() => {
    if (!formTouched) setForm(query.data ?? branding);
  }, [branding, formTouched, query.data]);
  const operations = useQuery({
    queryKey: ["admin", "system-operations"],
    queryFn: () =>
      api<{ factoryReset: boolean; portableBackup: boolean }>("/api/v1/admin/system-operations"),
  });
  const backupJobs = useQuery({
    queryKey: ["admin", "system-backups"],
    queryFn: () => api<PortableBackupJob[]>("/api/v1/admin/system-backup"),
    enabled: operations.data?.portableBackup === true,
    refetchInterval: (current) =>
      current.state.data?.some((job) => job.status === "queued" || job.status === "processing")
        ? 2_000
        : false,
  });
  const backupQueueActive = backupJobs.data?.some(
    (job) => job.status === "queued" || job.status === "processing",
  );
  const save = useMutation({
    mutationFn: () =>
      api<PlatformBranding>("/api/v1/admin/settings", {
        method: "PUT",
        body: JSON.stringify(form),
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(["platform", "branding"], saved);
      queryClient.setQueryData(["admin", "settings"], saved);
      setForm(saved);
      setFormTouched(false);
      await queryClient.invalidateQueries({ queryKey: ["platform", "branding"] });
    },
  });
  const requestBackup = useMutation({
    mutationFn: () =>
      api<PortableBackupJob>("/api/v1/admin/system-backup", {
        method: "POST",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "system-backups"] });
    },
  });
  const backupDownload = useMutation({
    mutationFn: (job: PortableBackupJob) => prepareSystemBackupDownload(job.id),
    onSuccess: (url) => {
      const link = document.createElement("a");
      link.href = url;
      document.body.append(link);
      link.click();
      link.remove();
      void queryClient.invalidateQueries({ queryKey: ["admin", "system-backups"] });
    },
  });
  const deleteBackup = useMutation({
    mutationFn: (job: PortableBackupJob) =>
      api<{ deleted: true }>(`/api/v1/admin/system-backup/${job.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setBackupDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "system-backups"] });
    },
  });
  const factoryReset = useMutation({
    mutationFn: () =>
      api<{ reset: true; signInAgain: true }>("/api/v1/admin/factory-reset", {
        method: "POST",
        body: JSON.stringify({ confirmation: resetConfirmation }),
      }),
    onSuccess: () => onFactoryReset(),
  });
  const backupDeleteDialogRef = useModalFocus(Boolean(backupDeleteTarget), () => {
    if (!deleteBackup.isPending) setBackupDeleteTarget(null);
  });
  const factoryResetDialogRef = useModalFocus(resetOpen, () => {
    if (!factoryReset.isPending) setResetOpen(false);
  });

  async function upload(file: File | undefined, target: "logo" | "favicon") {
    if (!file) return;
    setUploading(target);
    setUploadError(null);
    try {
      const asset = await uploadPlatformAsset<UploadedImage>(file);
      setForm((current) => ({
        ...current,
        [target === "logo" ? "logoUrl" : "faviconUrl"]: asset.publicUrl,
      }));
    } catch (error) {
      setUploadError(error);
    } finally {
      setUploading(null);
    }
  }

  const style = {
    "--brand-primary": form.primaryColor,
    "--brand-secondary": form.secondaryColor,
  } as CSSProperties;
  return (
    <div className="page-stack">
      <section className="page-actions">
        <div>
          <h2>Platform identity</h2>
          <p>
            Brand the platform and control the public address customers open on television displays.
          </p>
        </div>
        <button
          type="submit"
          form="platform-settings-form"
          className="button button--primary"
          disabled={save.isPending || !form.name}
        >
          {save.isPending ? "Saving…" : "Save platform settings"}
        </button>
      </section>
      <div className="settings-layout">
        <form
          id="platform-settings-form"
          className="card settings-form"
          onChangeCapture={() => setFormTouched(true)}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <label>
            <span>Platform name</span>
            <input
              value={form.name}
              maxLength={120}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
          </label>
          <div className="form-grid">
            <label>
              <span>Primary color</span>
              <div className="color-field">
                <ColorPicker
                  value={form.primaryColor}
                  onChange={(value) => setForm({ ...form, primaryColor: value })}
                />
                <code>{form.primaryColor}</code>
              </div>
            </label>
            <label>
              <span>Secondary color</span>
              <div className="color-field">
                <ColorPicker
                  value={form.secondaryColor}
                  onChange={(value) => setForm({ ...form, secondaryColor: value })}
                />
                <code>{form.secondaryColor}</code>
              </div>
            </label>
          </div>
          <div className="upload-grid">
            <label className="upload-control">
              <ImageUp size={20} />
              <span>
                <strong>{uploading === "logo" ? "Uploading…" : "Upload logo"}</strong>
                <small>PNG, JPEG, or WebP</small>
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={Boolean(uploading)}
                onChange={(event) => void upload(event.target.files?.[0], "logo")}
              />
            </label>
            <label className="upload-control">
              <ImageUp size={20} />
              <span>
                <strong>{uploading === "favicon" ? "Uploading…" : "Upload favicon"}</strong>
                <small>Square PNG recommended</small>
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={Boolean(uploading)}
                onChange={(event) => void upload(event.target.files?.[0], "favicon")}
              />
            </label>
          </div>
          <label>
            <span>Environment mode</span>
            <select
              value={form.environmentMode}
              onChange={(event) =>
                setForm({
                  ...form,
                  environmentMode: event.target.value as "development" | "production",
                })
              }
              aria-describedby="environment-mode-help"
            >
              <option value="development">Development</option>
              <option value="production">Production</option>
            </select>
            <small id="environment-mode-help">
              Records how maintainers and editing agents should treat this deployment. Production
              mode signals that live data and deployment actions require production safeguards.
            </small>
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={form.clientDashboardEnabled}
              onChange={(event) =>
                setForm({ ...form, clientDashboardEnabled: event.target.checked })
              }
            />
            <span>
              Enable client dashboard
              <small>
                Allow client sign-in and dashboard access. Administrators, public menus, and TV
                players remain available when this is turned off.
              </small>
            </span>
          </label>
          <label>
            <span>Public player URL</span>
            <input
              type="url"
              value={form.playerUrl ?? ""}
              onChange={(event) => setForm({ ...form, playerUrl: event.target.value || null })}
              placeholder={`${window.location.origin}/player`}
              aria-describedby="player-url-help"
            />
            <small id="player-url-help">
              Leave blank to use the server-side <code>PLAYER_URL</code> environment value, or{" "}
              <code>/player</code> when that value is also blank. Opening the exact saved HTTP(S)
              address renders the player directly, including a bare hostname.
            </small>
          </label>
          <ErrorNotice error={save.error ?? uploadError} />
          {save.isSuccess ? (
            <div className="notice notice--good">
              <ShieldCheck size={18} />
              <span>
                Platform settings were saved, the global player cache was refreshed, and active
                application surfaces were updated.
              </span>
            </div>
          ) : null}
        </form>
        <aside className="card brand-stage" style={style}>
          <span className="eyebrow">Live preview</span>
          <div className="brand-stage__mark">
            {form.logoUrl ? <img src={form.logoUrl} alt="" /> : <Server size={30} />}
          </div>
          <h3>{form.name || "Platform name"}</h3>
          <p>Branding updates will appear across the platform after saving.</p>
          <div className="brand-stage__browser">
            {form.faviconUrl ? <img src={form.faviconUrl} alt="" /> : <span />}
            <small>{form.name || "Platform"} · Dashboard</small>
          </div>
        </aside>
      </div>
      <section className="card system-operations-card" aria-labelledby="system-operations-title">
        <div>
          <span className="eyebrow">Docker recovery</span>
          <h2 id="system-operations-title">Backup and factory reset</h2>
          <p>
            Create a private, portable restore bundle or return this installation to its clean
            platform baseline.
          </p>
        </div>
        <div className="system-operation-grid">
          <article>
            <span className="system-operation-icon">
              <HardDrive size={22} />
            </span>
            <div>
              <h3>Portable Docker backup</h3>
              <p>
                Downloads source, database, stored assets, and required environment credentials.
                Extract it and run <code>docker compose up --build -d</code>.
              </p>
              <strong className="sensitive-copy">
                The archive contains secrets. Store it privately.
              </strong>
            </div>
            <button
              type="button"
              className="button button--secondary"
              disabled={
                operations.isLoading || !operations.data?.portableBackup || requestBackup.isPending
              }
              onClick={() => requestBackup.mutate()}
            >
              <FileClock size={17} />
              {requestBackup.isPending ? "Issuing request…" : "Request backup"}
            </button>
          </article>
          <article className="system-operation-danger">
            <span className="system-operation-icon">
              <RotateCcw size={22} />
            </span>
            <div>
              <h3>Factory reset</h3>
              <p>
                Permanently removes customers, users, slide types, content, TVs, uploaded assets,
                settings, and operational history. Only the configured initial administrator is
                recreated.
              </p>
            </div>
            <button
              type="button"
              className="button button--danger"
              disabled={operations.isLoading || !operations.data?.factoryReset || backupQueueActive}
              onClick={() => {
                setResetConfirmation("");
                setResetOpen(true);
              }}
            >
              <RotateCcw size={17} />
              Reset to factory defaults
            </button>
          </article>
        </div>
        <ErrorNotice
          error={
            operations.error ??
            backupJobs.error ??
            requestBackup.error ??
            backupDownload.error ??
            deleteBackup.error ??
            factoryReset.error
          }
        />
        {requestBackup.isSuccess ? (
          <div className="notice notice--good" role="status">
            <ShieldCheck size={18} />
            <span>Backup request issued. You can leave this page and download it when ready.</span>
          </div>
        ) : null}
        {operations.data?.portableBackup ? (
          <div className="backup-queue" aria-labelledby="backup-queue-title">
            <div className="backup-queue__heading">
              <div>
                <h3 id="backup-queue-title">Backup queue</h3>
                <p>Requests run one at a time. Ready archives expire after seven days.</p>
              </div>
              {backupJobs.isFetching ? (
                <span className="spinner" aria-label="Updating backups" />
              ) : null}
            </div>
            {backupJobs.isLoading ? (
              <div className="backup-queue__empty" role="status">
                Loading backup requests…
              </div>
            ) : backupJobs.data?.length ? (
              <div className="backup-queue__list" aria-live="polite">
                {backupJobs.data.map((job) => (
                  <article className="backup-queue__item" key={job.id}>
                    <div className="backup-queue__state">
                      <span className={`backup-status backup-status--${job.status}`}>
                        {job.status === "processing"
                          ? "Preparing"
                          : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </span>
                      <strong>{job.filename ?? "Portable Docker backup"}</strong>
                      <small>Requested {formatDate(job.createdAt)}</small>
                    </div>
                    <div className="backup-queue__details">
                      {job.status === "queued" ? <span>Waiting in queue</span> : null}
                      {job.status === "processing" ? (
                        <span>Creating and verifying archive…</span>
                      ) : null}
                      {job.status === "ready" ? (
                        <span>
                          {formatBytes(job.sizeBytes)} · Expires {formatDate(job.expiresAt)}
                        </span>
                      ) : null}
                      {job.status === "failed" ? (
                        <span className="backup-queue__error">
                          {job.errorMessage ?? "Backup creation failed."}
                        </span>
                      ) : null}
                      {job.status === "expired" ? (
                        <span>Request a new backup to download.</span>
                      ) : null}
                    </div>
                    <div className="backup-queue__actions">
                      {job.status === "ready" ? (
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          disabled={
                            backupDownload.isPending && backupDownload.variables?.id === job.id
                          }
                          onClick={() => backupDownload.mutate(job)}
                        >
                          <Download size={16} />
                          {backupDownload.isPending && backupDownload.variables?.id === job.id
                            ? "Starting…"
                            : "Download"}
                        </button>
                      ) : null}
                      {job.status !== "queued" && job.status !== "processing" ? (
                        <button
                          type="button"
                          className="button button--danger button--small"
                          aria-label={`Delete ${job.filename ?? "backup request"}`}
                          onClick={() => setBackupDeleteTarget(job)}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="backup-queue__empty">No backup requests yet.</div>
            )}
          </div>
        ) : null}
      </section>
      {backupDeleteTarget ? (
        <div className="modal-layer" role="presentation">
          <button
            type="button"
            className="modal-scrim"
            aria-label="Cancel backup deletion"
            disabled={deleteBackup.isPending}
            onClick={() => setBackupDeleteTarget(null)}
            tabIndex={-1}
          />
          <section
            ref={backupDeleteDialogRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-backup-title"
            tabIndex={-1}
          >
            <header className="modal-head">
              <div>
                <h2 id="delete-backup-title">Delete this backup?</h2>
                <p>The archive and its queue record will be permanently removed.</p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Cancel backup deletion"
                disabled={deleteBackup.isPending}
                onClick={() => setBackupDeleteTarget(null)}
                data-modal-initial-focus
              >
                ×
              </button>
            </header>
            <div className="modal-body">
              <div className="notice notice--error">
                <CircleAlert size={18} />
                <span>
                  {backupDeleteTarget.filename ?? "This backup request"} cannot be recovered after
                  deletion.
                </span>
              </div>
              <ErrorNotice error={deleteBackup.error} />
              <div className="modal-actions">
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={deleteBackup.isPending}
                  onClick={() => setBackupDeleteTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button button--danger"
                  disabled={deleteBackup.isPending}
                  onClick={() => deleteBackup.mutate(backupDeleteTarget)}
                >
                  <Trash2 size={16} />
                  {deleteBackup.isPending ? "Deleting…" : "Delete backup"}
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
      {resetOpen ? (
        <div className="modal-layer" role="presentation">
          <button
            type="button"
            className="modal-scrim"
            aria-label="Close factory reset"
            disabled={factoryReset.isPending}
            onClick={() => setResetOpen(false)}
            tabIndex={-1}
          />
          <section
            ref={factoryResetDialogRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="factory-reset-title"
            tabIndex={-1}
          >
            <header className="modal-head">
              <div>
                <h2 id="factory-reset-title">Reset the complete system?</h2>
                <p>This cannot be undone. Download a backup first if any data must be retained.</p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Close factory reset"
                disabled={factoryReset.isPending}
                onClick={() => setResetOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="modal-body">
              <div className="notice notice--error">
                <CircleAlert size={18} />
                <span>
                  All customers, client users, slides, playlists, TVs, uploaded assets, RSS data,
                  slide types, platform settings, and history will be erased. Demo data and built-in
                  slide types will not be recreated.
                </span>
              </div>
              <label className="factory-confirmation">
                <span>
                  Type <strong>RESET QASSIST TV</strong> to confirm
                </span>
                <input
                  autoFocus
                  value={resetConfirmation}
                  autoComplete="off"
                  onChange={(event) => setResetConfirmation(event.target.value)}
                />
              </label>
              <ErrorNotice error={factoryReset.error} />
              <div className="modal-actions">
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={factoryReset.isPending}
                  onClick={() => setResetOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button button--danger"
                  disabled={factoryReset.isPending || resetConfirmation !== "RESET QASSIST TV"}
                  onClick={() => factoryReset.mutate()}
                >
                  {factoryReset.isPending ? "Resetting system…" : "Permanently reset system"}
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
